import React, { useCallback, useEffect, useMemo, useState } from 'react';

const WEEK_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const SCHEDULE_OPTIONS = ['Weekly', 'Bi-Weekly', '4 Weekly', 'Monthly'];
const DATE_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric'
});

const startOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const toIsoDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addDays = (date, amount) => {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + amount);
  return next;
};

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

const parsePayDatePayload = (rawValue) => {
  if (!rawValue) return null;
  if (typeof rawValue === 'object') return rawValue;
  if (typeof rawValue === 'string') {
    const trimmed = rawValue.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return JSON.parse(trimmed);
      } catch (error) {
        console.warn('Unable to parse pay date payload', error);
        return null;
      }
    }
  }
  return null;
};

const generateWeeklyDates = (weekday) => {
  const index = WEEK_DAYS.indexOf(weekday);
  if (index === -1) {
    throw new Error('Select a weekday to continue');
  }
  const today = startOfToday();
  const jsTarget = (index + 1) % 7;
  const diff = (jsTarget - today.getDay() + 7) % 7;
  const firstOccurrence = addDays(today, diff);
  const limit = new Date(today);
  limit.setFullYear(limit.getFullYear() + 1);

  const dates = [];
  for (let current = firstOccurrence; current <= limit; current = addDays(current, 7)) {
    dates.push(toIsoDate(current));
  }
  return dates;
};

const clampDayToMonth = (requestedDay, year, month) => {
  const day = Number(requestedDay);
  const maxDay = getDaysInMonth(year, month);
  return Math.min(Math.max(day, 1), maxDay);
};

const generateMonthlyDates = (day) => {
  const requestedDay = Number(day);
  if (!Number.isFinite(requestedDay) || requestedDay < 1 || requestedDay > 31) {
    throw new Error('Enter a valid day of the month (1-31)');
  }
  const today = startOfToday();
  const limit = new Date(today);
  limit.setFullYear(limit.getFullYear() + 1);
  const dates = [];
  let cursor = new Date(today.getFullYear(), today.getMonth(), 1);
  while (cursor <= limit) {
    const occurrence = new Date(
      cursor.getFullYear(),
      cursor.getMonth(),
      clampDayToMonth(requestedDay, cursor.getFullYear(), cursor.getMonth())
    );
    if (occurrence >= today && occurrence <= limit) {
      dates.push(toIsoDate(occurrence));
    }
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return dates;
};

const generateSemiMonthlyDates = (firstDay, secondDay) => {
  const rawDays = [Number(firstDay), Number(secondDay)].filter(
    (value) => Number.isFinite(value) && value >= 1 && value <= 31
  );
  const uniqueDays = Array.from(new Set(rawDays)).sort((a, b) => a - b);
  if (!uniqueDays.length) {
    throw new Error('Provide at least one valid pay date (1-31)');
  }
  const today = startOfToday();
  const limit = new Date(today);
  limit.setFullYear(limit.getFullYear() + 1);
  const dates = [];
  let cursor = new Date(today.getFullYear(), today.getMonth(), 1);
  while (cursor <= limit) {
    for (const day of uniqueDays) {
      const occurrence = new Date(
        cursor.getFullYear(),
        cursor.getMonth(),
        clampDayToMonth(day, cursor.getFullYear(), cursor.getMonth())
      );
      if (occurrence >= today && occurrence <= limit) {
        dates.push(toIsoDate(occurrence));
      }
    }
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return dates.sort();
};

const generateFourWeeklyDates = (startIso) => {
  if (!startIso) {
    throw new Error('Select the next pay date to start the cycle');
  }
  const parsedStart = new Date(startIso);
  if (Number.isNaN(parsedStart.getTime())) {
    throw new Error('Enter a valid start date');
  }
  parsedStart.setHours(0, 0, 0, 0);
  const today = startOfToday();
  let current = parsedStart;
  while (current < today) {
    current = addDays(current, 28);
  }
  const limit = new Date(today);
  limit.setFullYear(limit.getFullYear() + 1);
  const dates = [];
  while (current <= limit) {
    dates.push(toIsoDate(current));
    current = addDays(current, 28);
  }
  return dates;
};

const formatPreviewDates = (dates = []) => {
  const preview = dates.slice(0, 18);
  const remainder = dates.length - preview.length;
  return {
    preview,
    remainder
  };
};

export default function PayDates({ canManage = false, onSaveComplete = () => {} }) {
  const [policies, setPolicies] = useState([]);
  const [selectedPolicyId, setSelectedPolicyId] = useState(null);
  const [schedule, setSchedule] = useState('Monthly');
  const [weeklyDay, setWeeklyDay] = useState('Friday');
  const [monthlyDay, setMonthlyDay] = useState(25);
  const [semiMonthly, setSemiMonthly] = useState({ first: 15, second: 30 });
  const [fourWeeklyStart, setFourWeeklyStart] = useState(toIsoDate(startOfToday()));
  const [generatedDates, setGeneratedDates] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadPayDates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('https://api.insightos.co.uk/api/paydates', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Failed to fetch pay dates');
      }
      const result = await response.json();
      const payload = Array.isArray(result.data) ? result.data : [];
      setPolicies(payload);
      setSelectedPolicyId((previous) => {
        if (!payload.length) return null;
        if (previous && payload.some((policy) => policy.id === previous)) {
          return previous;
        }
        return payload[0].id;
      });
    } catch (error) {
      console.error(error);
      setStatus({ type: 'error', message: error.message || 'Unable to load pay dates' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!canManage) return;
    loadPayDates();
  }, [canManage, loadPayDates]);

  const selectedPolicy = useMemo(
    () => policies.find((policy) => policy.id === selectedPolicyId) || null,
    [policies, selectedPolicyId]
  );

  useEffect(() => {
    if (!selectedPolicy) {
      setGeneratedDates([]);
      return;
    }
    const parsedPayload = parsePayDatePayload(selectedPolicy.payDatePayload || selectedPolicy.rawPayDate);
    const inferredSchedule = parsedPayload?.schedule || selectedPolicy.paySchedule || 'Monthly';
    setSchedule(inferredSchedule);
    setWeeklyDay(parsedPayload?.config?.weeklyDay || selectedPolicy.payDay || 'Friday');
    setMonthlyDay(parsedPayload?.config?.monthlyDay || 25);
    const firstSemi =
      parsedPayload?.config?.semiMonthly?.first ?? parsedPayload?.config?.semiMonthly?.second ?? 15;
    const secondSemi =
      parsedPayload?.config?.semiMonthly?.second ?? parsedPayload?.config?.semiMonthly?.first ?? 30;
    setSemiMonthly({ first: firstSemi, second: secondSemi });
    setFourWeeklyStart(
      parsedPayload?.config?.fourWeeklyStart ||
        parsedPayload?.config?.startDate ||
        toIsoDate(startOfToday())
    );
    setGeneratedDates(Array.isArray(parsedPayload?.dates) ? parsedPayload.dates : []);
  }, [selectedPolicy]);

  const handleGenerate = () => {
    if (!selectedPolicyId) {
      setStatus({ type: 'error', message: 'Select a policy before generating pay dates' });
      return;
    }
    try {
      let dates = [];
      if (schedule === 'Weekly') {
        dates = generateWeeklyDates(weeklyDay);
      } else if (schedule === 'Bi-Weekly') {
        dates = generateSemiMonthlyDates(semiMonthly.first, semiMonthly.second);
      } else if (schedule === '4 Weekly') {
        dates = generateFourWeeklyDates(fourWeeklyStart);
      } else {
        dates = generateMonthlyDates(monthlyDay);
      }
      setGeneratedDates(dates);
      setStatus({ type: 'info', message: `Generated ${dates.length} pay dates` });
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    }
  };

  const handleSave = async () => {
    if (!canManage) return;
    if (!selectedPolicyId) {
      setStatus({ type: 'error', message: 'Select a policy before saving' });
      return;
    }
    if (!generatedDates.length) {
      setStatus({ type: 'error', message: 'Generate pay dates before saving' });
      return;
    }
    setSaving(true);
    setStatus(null);
    const configPayload = {
      weeklyDay,
      monthlyDay,
      semiMonthly,
      fourWeeklyStart
    };
    try {
      const response = await fetch('https://api.insightos.co.uk/api/paydates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          policyId: selectedPolicyId,
          schedule,
          dates: generatedDates,
          config: configPayload
        })
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to save pay dates' }));
        throw new Error(error.message || 'Failed to save pay dates');
      }
      setStatus({ type: 'success', message: 'Pay dates saved successfully' });
      await loadPayDates();
      onSaveComplete();
    } catch (error) {
      console.error(error);
      setStatus({ type: 'error', message: error.message });
    } finally {
      setSaving(false);
    }
  };

  const previewData = useMemo(() => formatPreviewDates(generatedDates), [generatedDates]);

  if (!canManage) {
    return null;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="rounded-2xl border border-gray-200 bg-white/80 shadow-sm ring-1 ring-gray-100">
        <div className="border-b border-gray-100 px-4 py-5 sm:px-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-base font-semibold leading-6 text-gray-900">Pay Dates Generator</h2>
              <p className="mt-1 text-sm text-gray-600">
                Generate the next 12 months of pay runs and store them as JSON.
              </p>
            </div>
            {policies.length > 1 && (
              <select
                value={selectedPolicyId ?? ''}
                onChange={(event) => setSelectedPolicyId(Number(event.target.value))}
                className="mt-2 w-full rounded-md border-0 py-1.5 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:mt-0 sm:w-auto"
              >
                {policies.map((policy) => (
                  <option key={policy.id} value={policy.id}>
                    Policy #{policy.id}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="px-4 py-6 sm:px-6 lg:px-8">
          {loading ? (
            <p className="text-sm text-gray-500">Loading policies...</p>
          ) : policies.length === 0 ? (
            <p className="text-sm text-gray-500">Add a policy before configuring pay dates.</p>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-900">Schedule type</label>
                  <select
                    value={schedule}
                    onChange={(event) => setSchedule(event.target.value)}
                    className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
                  >
                    {SCHEDULE_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                {schedule === 'Weekly' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-900">Pay day</label>
                    <select
                      value={weeklyDay}
                      onChange={(event) => setWeeklyDay(event.target.value)}
                      className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
                    >
                      {WEEK_DAYS.map((day) => (
                        <option key={day} value={day}>
                          {day}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {schedule === 'Bi-Weekly' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-900">First pay date (day of month)</label>
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={semiMonthly.first}
                        onChange={(event) =>
                          setSemiMonthly((prev) => ({ ...prev, first: event.target.value }))
                        }
                        className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-900">Second pay date (day of month)</label>
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={semiMonthly.second}
                        onChange={(event) =>
                          setSemiMonthly((prev) => ({ ...prev, second: event.target.value }))
                        }
                        className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
                      />
                    </div>
                  </>
                )}

                {schedule === 'Monthly' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-900">Pay date (day of month)</label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={monthlyDay}
                      onChange={(event) => setMonthlyDay(event.target.value)}
                      className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
                    />
                  </div>
                )}

                {schedule === '4 Weekly' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-900">Next pay date (start of cycle)</label>
                    <input
                      type="date"
                      value={fourWeeklyStart}
                      onChange={(event) => setFourWeeklyStart(event.target.value)}
                      className="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-indigo-600 sm:text-sm"
                    />
                  </div>
                )}
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleGenerate}
                  className="rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  Generate preview
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save pay dates'}
                </button>
              </div>

              {status && (
                <p
                  className={`mt-3 text-sm ${
                    status.type === 'success'
                      ? 'text-green-600'
                      : status.type === 'error'
                      ? 'text-red-600'
                      : 'text-gray-600'
                  }`}
                >
                  {status.message}
                </p>
              )}

              {generatedDates.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-900">Upcoming pay dates</h3>
                    <span className="text-xs text-gray-500">Next 12 months</span>
                  </div>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {previewData.preview.map((date) => (
                      <div key={date} className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700">
                        {DATE_FORMATTER.format(new Date(date))}
                      </div>
                    ))}
                  </div>
                  {previewData.remainder > 0 && (
                    <p className="mt-2 text-xs text-gray-500">
                      +{previewData.remainder} additional dates scheduled within the next year
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
